/**
 * @module userManager
 */
import { Validator } from "jsonschema";
import errors from "../../errors";
import { userErrorCodes } from "./userErrorCodes";
import defaults, { services } from "../../../constants";
import { playbackSchema, mergeSchema } from "../../../schema/index";
import { setCookie } from "../../../utils/cookiesUtils";
import { t } from "web-utils-strings";
import {
  toDevicesCache,
  fromDevicesCache,
  addDeviceToCache,
  addDeviceToMemoryCache,
  toDevicesMemoryCache,
  fromDevicesMemoryCache,
  toUserCache,
  fromUserCache,
  clearUserCacheData,
  clearLayoutHeaders,
  toSubscriptionsStatusMemoryCache,
  fromSubscriptionsStatusMemoryCache,
  toAllActiveSubscriptionsDeepQueryCache,
  fromAllActiveSubscriptionsDeepQueryCache,
  toAvatarsCache,
  fromAvatarsCache
} from "./userCacheDecorators";
import { clearMediaCaches } from "../media/mediaCacheDecorators";
import { getErrorTrace } from "../../../errors/traces";
import { isTimeoutError } from "../../../errors/util";
import { cache } from "../../persistence/cache";
import { currentProfile, token, user } from "../../store";
import { TimeoutError } from "../../../errors/timeoutError";
import deepMerge from "../../../utils/deepMerge";
import { convertDataToQueryString } from "../../../utils/queryParams";
import { setUserLanguage } from "../../../utils/setLanguage";

import { deleteGuestProfile, updateUserData } from "../../manager/authentication/tokenOperations";
import allProfiles from "../../store/allProfiles";
import getJwtToken from "../authentication/jwtToken/jwtToken";

let isDeviceAdded = false;
const validator = new Validator();

/**
 * Validate the playback user object which contains the subtitles/audio settings for the user
 *
 * @param {Object} playback Receive a formatted body of the playback to validate before sending
 * to the web service in the following format:
 * {
 *   playback: {
 *     "audio": "en",
 *     "subtitles": "off"
 *   }
 * }
 */
function validatePreferencesParams(playback) {
  validator.addSchema(playbackSchema);
  const validation = validator.validate(playback, playbackSchema);

  if (validation.errors.length > 0) {
    throw Error(errors.user.INVALID_USER_PREFERENCES);
  }
}

/**
 * Validate if the token is present and if not to throw a login error
 * @return {boolean}
 */
function validateToken(headers) {
  if (!!headers && headers.Authorization) {
    return true;
  }
  throw new Error(errors.user.INVALID_USER_INFO);
}

/**
 * Returns the account information for the currently logged in user
 * @returns {Object}  An object representing the user account containing user information like fistName, lastName, email
 * address, parental level, language etc.
 */
async function get() {
  try {
    const response = await this.http.get(
      this.buildUrl("settings", services.user.user),
      await this.getHeaders.call(this)
    );
    response && response.data && setUserLanguage.call(this, response.data);
    const profiles = response && response.data && response.data.profiles;
    const spxToken = await token.getSPXToken.call(this);
    if (profiles && profiles.length > 0) {
      profiles[0].token = spxToken;
    }
    profiles && allProfiles.update.call(this, profiles);
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
    throw Error(`${errors.user.GENERIC_ERROR} - ${error}`);
  }
}

/**
 * Refresh the user data of the local cache
 */
async function refreshCachedUserData() {
  try {
    const userData = await get.call(this, { avoidCache: true });
    const filteredUserData = getUserData(userData);
    setUserCacheData.call(this, filteredUserData);
    return filteredUserData;
  } catch (error) {
    throw Error(`${errors.user.GENERIC_ERROR} - ${error}`);
  }
}

/**
 * Performs a PUT request to the backend with an updated set of properties to be set in the user account. The properties
 * that will be updated in the backend will be those that appear in the user object received as a parameter and will
 * have to match if the object anatomy used in the backend. For example, if the received user object looks like this:
 * const user = {
 *   firstName: "John",
 *   lastName: "Doe",
 *   settings: {
 *     language: "fr",
 *     parental: 18
 *   }
 * }
 * The user account will be updated by changing the values of the properties firstName, lastName, settings.language and
 * settings.parental. The rest of the properties that are already present in the user account will remain unchanged.
 * @param {Object} user An object containing a set of properties with their new values to be stored in the account. The
 * properties structure must match the object's structure stored in the backend.
 */
async function update(user) {
  try {
    const { updateCache = true, profileJwt = "" } = user;
    const headers = await this.getSelectedProfileHeaders.call(this, { updateCache, profileJwt });
    const response = await this.http.put(this.buildUrl("settings", services.user.user), user, headers);
    await updateUserCacheData.call(this, getUserData(response.data));
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

async function update_v1(user) {
  try {
    const { updateCache = true, profileJwt = "" } = user;
    const headers = await this.getSelectedProfileHeaders.call(this, { updateCache, profileJwt });
    const response = await this.http.put(this.buildUrl("settings", services.user.user_v1), user, headers);
    await updateUserCacheData.call(this, getUserData(response.data));
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Updates user related data stored in cache.
 * @param {Object} userData - User new data object to be stored.
 * @param {String} newToken - User new token.
 */
async function updateUserCacheData(userData, newToken, needsLayoutCacheReset = false) {
  if (this.localCache && this.localCache.auth) {
    const cachedUserData = (await cache.get("user", { database: "auth" })) || {};
    const data = deepMerge(cachedUserData, userData);
    setUserCacheData.call(this, data);
    newToken && token.updateToken.call(this, newToken);
  }
  await clearUserLayoutCacheData.call(this, needsLayoutCacheReset);
}

/**
 * Sets user related data stored in cache.
 * @param {Object} data - User new data object to be stored.
 */
function setUserCacheData(data) {
  cache.set("user", data, {
    database: "auth",
    expiresIn: this.tokenExpirationTime
  });
}

/**
 * Removes all the locally cached layout's data that belongs to languages other than the current one. For example, if
 * the app has locally cached layouts for a logged user in arabic and french and the user changes the language to
 * english, the layouts' data belonging to arabic and french will be removed. It's important to note that this function
 * only operates on cached layouts for logged in users.
 */
function clearUserLayoutCacheData(needsClearAll) {
  if (this.localCache && this.localCache.layouts) {
    this.supportedLanguages.forEach(language => {
      if (!!needsClearAll || (!needsClearAll && language !== this.lang)) {
        cache.clearAllFilteredKeysFromDatabase("layouts", `${language}-logged`);
      }
    });
  }
}

/**
 * Changes the parental control for the user.
 * @param {number}  level Number for the new parental control level
 */
async function changeParentalLevel(parentalLevel, password) {
  if (await this.authenticationManager.isLoggedIn()) {
    if (!this.parentalControlLevels.includes(parentalLevel)) {
      throw Error(`${errors.user.parentalControl.INVALID_LEVEL} (${parentalLevel})`);
    }
    const levels = this.parentalControlLevels;
    const isUpgradeLevel = levels.indexOf(parentalLevel) > levels.indexOf(this.parentalLevel);
    if (isUpgradeLevel && !password) {
      throw Error(errors.user.parentalControl.PASSWORD_REQUIRED);
    }
    try {
      const data = await update.call(this, {
        settings: {
          parentalControl: parentalLevel
        },
        password,
        usePassword: isUpgradeLevel
      });
      this.__updatePCL(parentalLevel, this);
      const userData = getUserData(data, true);
      const { token } = userData;
      delete userData["token"];
      await updateUserCacheData.call(this, userData, token, true);
      return data;
    } catch (error) {
      console.log("Could not change parental control level in the account");
      this.handleErrorResponse(error);
      throw error;
    }
  }
}

/**
 * Sets initial parental level after logging in or resets its value to its default.
 */
function setInitialParentalLevel(parentalLevel = defaults.parentalLevel) {
  this.__updatePCL(parentalLevel, this);
}

/**
 * Returns the user data to be set in cache
 * @param {Object} data user data object that contains all information
 * @param {boolean} returnToken indicates if the token should be included in the response object
 */
function getUserData(data, returnToken = false) {
  const {
    globalUserId,
    firstName,
    lastName,
    emailAddress,
    locale,
    settings: { accountStatus, parentalControl, addons, requestedState },
    token
  } = data;
  return {
    globalUserId,
    firstName,
    lastName,
    emailAddress,
    locale,
    settings: { accountStatus, parentalControl, ...(addons && { addons }), requestedState },
    ...(returnToken && { token })
  };
}

/**
 * Sets a new language for the running SDK instance and updates the account language to change the language in every
 * device.
 * @param {String}  language Two character language code that needs to be set ('en' for English, 'fr' for French, 'ar'
 * for Arabic, etc)
 */
async function setLanguage(language) {
  this.lang = language;
}

/**
 * Gets user devices.
 */
async function getDevices() {
  try {
    const response = await this.http.get(this.buildUrl("settings", services.devices), await this.getHeaders.call(this));
    this.maxUserDevices = response.data.maxDeviceLimit;
    localStorage.setItem("maxDevices", response.data.maxDeviceLimit);
    return response.data.devices;
  } catch (error) {
    this.handleErrorResponse(error);
    throw Error(`${errors.user.devices.GENERIC_ERROR} - ${error}`);
  }
}

/**
 * Add a new device to the user devices list.
 * @param {Object} device - Device properties object.
 */
async function addDevice(device) {
  try {
    const response = await this.http.post(
      this.buildUrl("settings", services.devices),
      device,
      await this.getHeaders.call(this)
    );
    isDeviceAdded = true;
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === userErrorCodes.DEVICE_ALREADY_ADDED_STATUS_CODE) {
      return (isDeviceAdded = true);
    }
    let errorToThrow;
    if (isTimeoutError(error)) {
      errorToThrow = new TimeoutError(`${errors.user.devices.ADD_DEVICE_TIMEOUT} - ${error}`);
    } else {
      const errorTrace = getErrorTrace("addDeviceError");
      this.reportsManager.sendReport.call(
        this,
        { env: this.environment, level: "error", event: "Playback" },
        {
          report: errorTrace.code,
          status: "error",
          parameter: {
            errorCode: errorTrace.code,
            dump: { trace: error.message, placeInCode: errorTrace.dump.placeInCode }
          }
        }
      );
      errorToThrow = new Error();
      errorToThrow.message = `${errors.user.devices.DEVICE_NOT_ADDED} - ${error}`;
      errorToThrow.code = error.response && error.response.status;
      errorToThrow.error_code = error.response && error.response?.data?.error_code;
    }
    throw errorToThrow;
  }
}

function resetDeviceAdded() {
  isDeviceAdded = false;
}

/**
 * Returns if the user has the maximum number of devices reached
 */
async function isMaxDevicesReached(config) {
  try {
    const userDevices = await getDevicesWithMemoryAndCache.call(this, config);
    return userDevices.length === this.maxUserDevices;
  } catch (e) {
    this.handleErrorResponse(e);
    console.log(e);
    return false;
  }
}

/**
 * Returns if the actual device is already in the list of user devices
 */
async function isDeviceRegistered(config) {
  try {
    const userDevices = await getDevicesWithMemoryAndCache.call(this, config);
    const deviceId = await this.device.getId();
    return userDevices.some(device => device.clientIdentifiers.WMDRM === deviceId);
  } catch (e) {
    this.handleErrorResponse(e);
    console.log(e);
  }
}

/**
 * Returns if the actual device have to be included the list of user devices
 */
async function canDeviceBeAdded() {
  try {
    const userDevices = await getDevicesWithMemoryAndCache.call(this);
    const hasDevice = await isDeviceRegistered.call(this);
    return userDevices.length < this.maxUserDevices && !hasDevice && !isDeviceAdded;
  } catch (e) {
    console.log(e);
  }
}

/**
 * Performs several checks in order to know whether a device should be allowed playing content or not. This function
 * does the same as isMaxDevicesReached, isDeviceRegistered and canDeviceBeAdded but combined in a single call. Also,
 * it returns a richer information in one shot because the return value is an object with all the information gathered
 * during the execution of the function.
 * @param {Object} config Configuration object to define how the execution will be done. Check possible properties.
 * @param {Boolean} config.avoidCache If true, this function will always call backend to get devices information. In
 * other case if any of the caches can return the devices list, the cache will be used.
 * @return {Promise<{
 *   deviceId: (string),
 *   deviceAlreadyRegistered: boolean,
 *   freeDeviceSlotsExist: boolean,
 *   maximumDevicesAllowed: number}>}
 */
async function checkDeviceCanPlay(config) {
  try {
    const deviceId = await this.device.getId();
    const userDevices = await getDevicesWithMemoryAndCache.call(this, config);
    this.maxUserDevices = localStorage.getItem("maxDevices")
      ? parseInt(localStorage.getItem("maxDevices"))
      : this.maxUserDevices;
    const freeDeviceSlotsExist = userDevices.length < this.maxUserDevices;
    const deviceAlreadyRegistered =
      isDeviceAdded || userDevices.some(device => device.clientIdentifiers.WMDRM === deviceId);
    const maximumDevicesAllowed = this.maxUserDevices;
    return {
      deviceId,
      deviceAlreadyRegistered,
      freeDeviceSlotsExist,
      maximumDevicesAllowed
    };
  } catch (e) {
    this.handleErrorResponse(e);
    console.log(e);
    return e;
  }
}

/**
 * Do a delete request to remove a user device
 *
 * @param {string} deviceId Id of the device to delete
 * @return {Promise}
 */
async function deleteDevice(deviceId) {
  try {
    const url = `${this.buildUrl("settings", services.devices)}?deviceId=${deviceId}`;
    return await this.http.delete(url, await this.getHeaders.call(this));
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Performs a GET request to the user preferences services to get the latest data set to the
 * current user to keep it synced along all devices
 *
 * @returns {Object} Playback user preferences object
 */
async function getPreferences() {
  try {
    const headers = await this.getSelectedProfileHeaders.call(this);
    // validateToken(headers);

    const preferences = await this.http.get(this.buildUrl("settings", services.user.preferences), headers);
    return preferences.data;
  } catch (e) {
    this.handleErrorResponse(e);
    throw Error(errors.user.INVALID_USER_INFO);
  }
}

/**
 * Performs a POST request to the user preferences services and returning the updated playback object which
 * stored in the WS
 *
 * @param {Object} playback get the params to update user playback prefernces in the following format
 * Example of params object:
 * {
 *   audio: "en",
 *   subtitles: "ar"
 * }
 *
 * @returns {Object} Playback user preferences object
 */
async function updatePreferences(playback = {}) {
  const playbackBody = { playback };
  const headers = await this.getSelectedProfileHeaders.call(this);
  validatePreferencesParams(playbackBody);
  //validateToken(headers);

  const preferences = await this.http.post(this.buildUrl("settings", services.user.preferences), playbackBody, headers);
  return preferences.data;
}

async function getBilling() {
  try {
    const response = await this.http.get(
      this.buildUrl("settings", services.user.billing),
      await this.getHeaders.call(this)
    );
    return response.data;
  } catch (error) {
    throw Error(`${errors.user.devices.GENERIC_ERROR} - ${error}`);
  }
}

const getDevicesWithMemoryAndCache = toDevicesCache(
  toDevicesMemoryCache(fromDevicesMemoryCache(fromDevicesCache(getDevices)))
);

/**
 * Validates the object which will be sent in /merge request.
 * @param {Object} mergeInfo - Merge data object.
 * @private
 */
function mergeValidation(mergeInfo) {
  const validation = validator.validate(mergeInfo, mergeSchema);
  if (validation.errors.length) {
    throw Error(`${errors.user.mergeAccount.INVALID_MERGE_ACCOUNT_PARAMS} - ${validation.errors.join(" ")}`);
  }
}

/**
 * Merges other accounts (Facebook, etc.) with existing STARZ PLAY account
 * @param {Object} config Merge data object.
 */
async function mergeAccounts(config = {}) {
  // FIXME This function should not accept the social token. It should be kept in internal memory of the SDK instead
  mergeValidation(config);
  try {
    const response = await this.http.post(
      this.buildUrl("settings", services.user.merge),
      config,
      await this.getHeaders.call(this)
    );
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Performs a GET request to the service to get the status of the user's addons subscriptions
 *
 * @returns {Object} User's addons subscriptions status object
 */
async function getSubscriptionsStatus(data) {
  try {
    const queries = data && data.addonName ? `addonName=${data.addonName}` : null;
    const response = await this.http.get(
      `${this.buildUrl("settings", services.user.subscriptionsStatus)}${!!queries ? `?${queries}` : ""}`,
      await this.getHeaders.call(this)
    );
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

async function getTVODAssetsStatus(data, userId) {
  try {
    let queries = convertDataToQueryString(data);
    const response = await this.http.get(
      `${this.buildUrl("settings", services.user.tvodAssetsStatus)}${
        queries?.length > 0 ? `?userId=${userId}&${queries}` : `?userId=${userId}`
      }`,
      await this.getHeaders.call(this)
    );
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}
async function updateTVODAssetsStatus(data) {
  try {
    const queries = convertDataToQueryString(data);
    const response = await this.http.put(
      `${this.buildUrl("settings", services.user.tvodAssetsStatus)}${!!queries ? `?${queries}` : ""}`,
      await this.getHeaders.call(this)
    );
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}
async function getTVODProductPrices(data) {
  try {
    const queries = convertDataToQueryString(data);
    const response = await this.http.get(
      `${this.buildUrl("settings", services.user.tvodProductPrices)}${!!queries ? `?${queries}` : ""}`,
      await this.getHeaders.call(this)
    );
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Triggers a request to activate the addon subscription
 * @param {Object} data
 * @param {String} data.addonName - Mandatory. Name of the addon to subscribe.
 * @param {Object} data.addonSubscriptionData - Mandatory. Data object that contains the subscription information related.
 * @param {String} data.addonSubscriptionData.paymentPlanId - Mandatory. Id of the payment plan.
 * @param {String} data.addonSubscriptionData.paymentMethodName - Optional. Name of the payment method.
 * @param {Object} data.addonSubscriptionData.paymentMethodParams - Optional. Data object that contains the payment method information related
 * @param {String} data.addonSubscriptionData.paymentMethodParams.language - Optional. Language
 * @param {String} data.addonSubscriptionData.paymentMethodParams.pin - Optional. Pin of the card used in the payment
 * @param {String} data.addonSubscriptionData.paymentMethodParams.token - Optional. Token used in the payment
 * @param {String} data.addonSubscriptionData.paymentMethodParams.customerBillingEmail - Optional. User's emailcustomerBillingEmail
 * @public
 */
async function activateAddonSubscription(data = {}) {
  try {
    const timeout = this.networkRequestTimeouts.activateChannel;
    const response = await this.http.post(this.buildUrl("settings", services.user.addonActivation), data, {
      ...(timeout !== undefined && { timeout }),
      ...(await this.getHeaders.call(this))
    });
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Deactivate the user subscription
 * @param {Object} requestBody
 * @param {String} requestBody.deactivationReason Reason for deactivation
 * @param {String} requestBody.requestedState State of deactivation
 * @param {String} requestBody.deactivationText Additional text mentioning reason of deactivation
 * @public
 */
async function deactivateSubscription(requestBody) {
  try {
    const response = await this.http.post(
      this.buildUrl("settings", services.user.subscriptionDeactivation),
      requestBody,
      await this.getHeaders.call(this)
    );
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Reactivate the user subscription
 * @param {Object} requestBody
 * @private
 */
async function reactivateSubscription(requestBody) {
  try {
    const url = this.buildUrl("settings", services.user.subscriptionReactivation);
    const response = await this.http.post(url, requestBody, await this.getHeaders.call(this));
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Reactivate the user addon
 * @param {Object} requestBody
 * @private
 */
async function reactivateAddon(requestBody) {
  try {
    const url = this.buildUrl("settings", services.user.addonReactivation);
    const response = await this.http.post(url, requestBody, await this.getHeaders.call(this));
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Triggers a request to deactivate the addon subscription
 * @param {Object} data
 * @param {String} data.addonName - Mandatory. Name of the addon to unsubscribe.
 * @param {Object} data.addonsDeactivationData - Mandatory. Data object that contains the desubscription information related.
 * @param {String} data.addonsDeactivationData.deactivationReason - Optional. reason of channel deactivation.
 * @param {String} data.addonsDeactivationData.deactivationText - Mandatory. Text of deactivation.
 * @public
 */

async function deactivateAddonSubscription(data = {}) {
  try {
    const timeout = this.networkRequestTimeouts.activateChannel;
    const response = await this.http.post(this.buildUrl("settings", services.user.addonDeactivation), data, {
      ...(timeout !== undefined && { timeout }),
      ...(await this.getHeaders.call(this))
    });
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Generate pin and if not to throw error
 * @return Array<Object> A list of objects representing the validation details
 */
async function generatePin() {
  try {
    let headers = await this.getHeaders.call(this);
    const guestProfileData = this.guestProfie();
    if (guestProfileData && Object.keys(guestProfileData).length > 0) {
      const xGuid = guestProfileData?.globalUserId;
      const xToken = guestProfileData["guest-spx-token"];
      const additionalHeader = this.buildLinktvAdditionheaders(xGuid, xToken);
      headers = { headers: { ...headers.headers, ...additionalHeader.headers } };
    }
    const response = await this.http.get(this.buildUrl("settings", services.user.generatePin), headers);

    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * login using pin and if not to throw error
 * @return Array<Object> A list of objects representing the validation details
 */
async function loginPin(data) {
  try {
    const response = await this.http.post(
      this.buildUrl("settings", services.user.loginPin),
      data,
      await this.getHeaders.call(this)
    );
    if (response.data.token && response.data.token !== "false") {
      // upadated cheeck from webToken to token. as only token field is coming from BE for unsuccessful response
      deleteGuestProfile();
      // to clear Index DB layouts cache of logged out user
      cache.clear({ database: "layouts" });

      const jwtCookie = this.jwtCookie;
      jwtCookie ? setCookie(jwtCookie.name, response.data.webToken, jwtCookie.expirationTime) : null;
      setCookie("spx-token", response.data.token, jwtCookie.expirationTime);
      token.updateSPXToken.call(this, response.data.token);
      token.updateToken.call(this, response.data.webToken);
      updateUserData.call(this, response.data.webToken, response.data.uid);
    }

    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Get concurrent streaming devices and if not to throw error
 * @return Array<Object> A list of objects containg concurrent streaming details
 */
async function getConcurrency() {
  try {
    const response = await this.http.get(
      this.buildUrl("settings", services.user.concurrency),
      await this.getHeaders.call(this)
    );
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

function updateUserToken(userToken) {
  try {
    token.updateToken.call(this, userToken);
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

function updateUserCookie(token) {
  try {
    const jwtCookie = this.jwtCookie;
    jwtCookie ? setCookie(jwtCookie.name, token, jwtCookie.expirationTime) : null;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Validate if the pin is present and if not to throw error
 * @return Array<Object> A list of objects representing the validation details
 */
async function validatePin(data) {
  try {
    const response = await this.http.post(
      this.buildUrl("settings", services.user.validatePin),
      data,
      await this.getHeaders.call(this)
    );

    if (data && data.signupFlow && response && response.data && response.data.userData) {
      clearUserCacheData();

      const jwtCookie = this.jwtCookie;
      const spxToken = response.data.userData.spxToken;
      const globalUserId = response.data.userData.userId;
      const jwtToken = response.data.userData.jwtToken;
      const config = {};
      config.cookie = jwtCookie;

      setCookie("spx-token", spxToken, jwtCookie.expirationTime);
      updateUserData.call(this, jwtToken, globalUserId, config);
    }

    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

const DEVICE_ID = "AAQQQQ";
/**
 * Creates request body object to be sent in eVoucher eligibility request.
 */
async function createEvoucherEligibilityRequestBody(baseVoucherCode, addonVoucherCode) {
  return {
    deviceId: DEVICE_ID,
    modelId: (await this.systemManager.device.getModelCode()) || "",
    baseVoucherCode,
    addonVoucherCode
  };
}

/**
 * Checks user's device voucher eligibility.
 */
export async function checkEvoucherEligiblity(country, duration, platform = "samsung") {
  try {
    await checkEvoucherValidation.call(this);
  } catch (error) {
    throw error;
  }
  try {
    const model_id = DEVICE_ID;
    const response = await this.http.get(
      `${this.backendHosts.settings}${services.eVoucherEligibility}?model=${model_id}&country=${country}&platform=${platform}&duration=${duration}`
    );
    if (response.data.status !== "VALID") {
      throw new Error("Eligibility status is invalid");
    }
    return response.data;
  } catch (error) {
    error.customCode = "EvoucherEligibiltyFailed";
    error.customMessage = "Couldn't retrieve voucher eligiblity information";
    throw error;
  }
}

/**
 * Checks user's device validity.
 */
export async function checkEvoucherDeviceValidity(platform = "samsung") {
  try {
    const response = await this.http.get(
      `${this.backendHosts.settings}${services.eVoucherDeviceValidation}?platform=${platform}`
    );

    const deviceYear = (await this.device.getYear()) || 2020;

    return deviceYear >= response.data.validFrom && deviceYear <= response.data.validTo ? true : false;
  } catch (error) {
    throw new Error(`Couldn't retrieve voucher device validity information -> ${error}`);
  }
}

/**
 * Checks user's device voucher validation.
 */
export async function checkEvoucherValidation() {
  try {
    const model_id = DEVICE_ID;
    const response = await this.http.get(
      `${this.backendHosts.settings}${services.eVoucherValidation}?deviceId=${model_id}`
    );

    if (response.data.status !== "VALID") {
      throw new Error("Validation status is invalid");
    }
    return response.data;
  } catch (error) {
    error.customCode = "EvoucherValidationFailed";
    error.customMessage = "Couldn't retrieve voucher validtion information";
    throw error;
  }
}

/**
 * Checks user's device voucher eligibility.
 */
export async function eVoucherProvision(baseVoucherCode, addonVoucherCode) {
  try {
    const timeout = this.networkRequestTimeouts.eVoucherProvision;
    const response = await this.http.post(
      `${this.backendHosts.settings}${services.eVoucherProvision}`,
      await createEvoucherEligibilityRequestBody.call(this, baseVoucherCode, addonVoucherCode),
      {
        ...(timeout !== undefined && { timeout }),
        ...(await this.getHeaders.call(this))
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(`Couldn't provision the voucher -> ${error}`);
  }
}

/**
 * Returns payment history
 * @private
 */

export async function getPaymentHistory(pastMonths) {
  try {
    const url = `${this.buildUrl("settings", services.user.paymentHistory)}?pastMonths=${pastMonths}`;
    const response = await this.http.get(url, await this.getHeaders.call(this));
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Returns active subscriptions
 * @private
 */

export async function getAllActiveSubscriptionsDeepQuery() {
  try {
    const activeSubsTimeout = this.networkRequestTimeouts.activeSubscription;
    const reqHeaders = await this.getHeaders.call(this);
    const response = await this.http.get(this.buildUrl("settings", services.user.activeSubscriptions), {
      ...reqHeaders,
      ...(activeSubsTimeout !== undefined && { timeout: activeSubsTimeout })
    });
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/*
 * Returns the discount information for the currently logged in user
 * @returns {Object}  An object representing the user's discount information
 * Private
 */
async function getUserDiscountEligibility(requestBody = {}) {
  try {
    const response = await this.http.post(
      this.buildUrl("settings", services.user.discountEligibility),
      requestBody,
      await this.getHeaders.call(this)
    );
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
    throw Error(`${errors.user.GENERIC_ERROR} - ${error}`);
  }
}

/**
 * Performs a POSt request to the backend to add a new profile with profile data
 * that will be updated in the backend will be those that appear in the user object received as a parameter and will
 * @param {Object} data An object containing a set of properties with their values to be stored in the for new user profile. The
 * properties structure must match the object's structure stored in the backend.
 */
async function addProfile(data) {
  try {
    const response = await this.http.post(
      `${this.buildUrl("settings", services.user.spx.addProfile)}`,
      data,
      await this.getHeaders.call(this)
    );
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Performs a DELETE request to the backend to delete a profile
 * that will be updated in the backend will be those that appear in the user object received as a parameter and will
 * @param {Object} data An object containing a set of properties with their values to be stored in the for new user profile. The
 * properties structure must match the object's structure stored in the backend.
 */
async function deleteProfile(data) {
  try {
    // Security Threat Alert
    // Need to encrypt password in data payload
    const params = {
      headers: {
        ...(await this.getSelectedProfileHeaders.call(this)).headers
      },
      data: {
        ...data
      }
    };
    const response = await this.http.delete(`${this.buildUrl("settings", services.user.spx.deleteProfile)}`, params);
    const selectedProfile = await getCurrentProfile();
    if ((selectedProfile && selectedProfile.profileId) === data.profileId) {
      await currentProfile.deleteProfile();
    }
    return response.status;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

// move this out of user service as it is not user specific
/**
 * Gets user avatar.
 */
async function getAvatars() {
  try {
    const response = await this.http.get(this.buildUrl("spxV1_0", t(services.spx.getAvatars)));

    const urls =
      response.data &&
      response.data.map(url => {
        let updatedURL = null;
        if (url.indexOf("https://starzplay-prod-ssl.akamaized.net") !== -1) {
          updatedURL = url.replace("https://starzplay-prod-ssl.akamaized.net", "http://mena-img-cdn-lb.aws.playco.com");
        } else {
          updatedURL = url.replace("https", "http");
        }
        return updatedURL;
      });

    return urls;
  } catch (error) {
    this.handleErrorResponse(error);
    throw Error(`${errors.genericNetworkError} - ${error}`);
  }
}

async function getCurrentProfile() {
  try {
    const currentProfileData = await currentProfile.get.call(this);
    return currentProfileData;
  } catch (error) {
    this.handleErrorResponse(error);
    throw Error(`${errors.genericNetworkError} - ${error}`);
  }
}

async function updateCurrentProfile(profileData) {
  try {
    if (profileData?.isGenerateJWTToken) {
      profileData["profileJwtToken"] = (await getJwtToken.call(this, profileData))?.profileJwtToken;
    } else {
      profileData["profileJwtToken"] = await currentProfile.getToken.call(this);
    }
    const currentProfileData = await currentProfile.update.call(this, profileData);
    return currentProfileData;
  } catch (error) {
    this.handleErrorResponse(error);
    throw Error(`${errors.genericNetworkError} - ${error}`);
  }
}

async function deleteCurrentProfile() {
  try {
    await currentProfile.deleteProfile();
  } catch (error) {
    this.handleErrorResponse(error);
    throw Error(`${errors.genericNetworkError} - ${error}`);
  }
}

async function getAllProfiles() {
  try {
    const allProfilesData = await allProfiles.get.call(this);
    return allProfilesData;
  } catch (error) {
    this.handleErrorResponse(error);
    throw Error(`${errors.genericNetworkError} - ${error}`);
  }
}

async function updateAllProfiles(profiles) {
  try {
    if (profiles && profiles.length > 0 && !profiles[0].token) {
      const spxToken = await token.getSPXToken.call(this);
      profiles[0].token = spxToken;
    }
    const profilesList = await allProfiles.update.call(this, profiles);
    return profilesList;
  } catch (error) {
    this.handleErrorResponse(error);
    throw Error(`${errors.genericNetworkError} - ${error}`);
  }
}

async function updateSelectedProfileFromAllProfiles(profileData) {
  try {
    const allProfilesData = await allProfiles.get.call(this);
    const selectedProfile = allProfilesData.find(profile => profile.profileId === profileData.profileId);
    if (selectedProfile) {
      allProfilesData[allProfilesData.indexOf(selectedProfile)] = profileData;
      const spxToken = await token.getSPXToken.call(this);
      if (allProfilesData && allProfilesData.length > 0) {
        allProfilesData[0].token = spxToken;
      }
      await allProfiles.update.call(this, allProfilesData);
    }
    return allProfilesData;
  } catch (error) {
    this.handleErrorResponse(error);
    throw Error(`${errors.genericNetworkError} - ${error}`);
  }
}

async function addUpdatePin(data) {
  try {
    const response = await this.http.post(
      `${this.buildUrl("settings", services.user.spx.addUpdatePin)}`,
      data,
      await this.getSelectedProfileHeaders.call(this)
    );
    return response.status;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

async function verifyPin(data) {
  try {
    const response = await this.http.post(
      `${this.buildUrl("settings", services.user.spx.verifyPin)}`,
      data,
      await this.getSelectedProfileHeaders.call(this)
    );
    return response.status;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

async function disablePin(data) {
  try {
    const response = await this.http.post(
      `${this.buildUrl("settings", services.user.spx.disablePin)}`,
      data,
      await this.getSelectedProfileHeaders.call(this)
    );
    return response;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

async function updateIndexDBUser(updatedUser) {
  try {
    await user.update.call(this, updatedUser);
  } catch (error) {
    this.handleErrorResponse(error);
    throw Error(`${errors.genericNetworkError} - ${error}`);
  }
}

async function getProfile(data) {
  try {
    const { hash, guid: profileId, country, timestamp } = data;
    const response = await this.http.get(
      `${this.buildUrl(
        "settings",
        t(services.user.spx.getProfile)
      )}?hash=${hash}&profileId=${profileId}&country=${country}&timestamp=${timestamp}`,
      await this.getSelectedProfileHeaders.call(this)
    );
    return response;
  } catch (error) {
    this.handleErrorResponse(error);
    throw Error(`${errors.genericNetworkError} - ${error}`);
  }
}

/**
 * Check user password
 */
async function verifyPassword(data) {
  try {
    const response = await this.http.post(
      this.buildUrl("settings", t(services.user.spx.verifyPassword)),
      data,
      await this.getHeaders.call(this)
    );
    return response;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

export default {
  get: toUserCache(fromUserCache(get)),
  update,
  update_v1,
  setLanguage: clearLayoutHeaders(clearMediaCaches(setLanguage)),
  changeParentalLevel: clearLayoutHeaders(clearMediaCaches(changeParentalLevel)),
  setInitialParentalLevel,
  /**
   * Gets the devices attached to the current user account. The source from where the data is taken is from local cache
   * if there is any devices data available on it, and from th backend in case there is no locally stored information.
   * Also, it's possible to bypass the local cache and force a network call by passing a config object with a property
   * called avoidCache = true.
   * @returns Array<Object> A list of objects representing the devices attached to the account
   */
  getDevices: getDevicesWithMemoryAndCache,
  addDevice: addDeviceToCache(addDeviceToMemoryCache(addDevice)),
  resetDeviceAdded,
  isMaxDevicesReached,
  canDeviceBeAdded,
  isDeviceRegistered,
  checkDeviceCanPlay,
  deleteDevice,
  getPreferences, // method only use in player
  // : toPreferencesCache(
  //   toPreferencesMemoryCache(fromPreferencesMemoryCache(fromPreferencesCache(getPreferences)))
  // ),
  // updatePreferences: toPreferencesMemoryCache(toUpdatePreferencesCache(updatePreferences)),
  updatePreferences,
  getBilling: getBilling,
  userErrorCodes,
  mergeAccounts,
  getSubscriptionsStatus: toSubscriptionsStatusMemoryCache(fromSubscriptionsStatusMemoryCache(getSubscriptionsStatus)),
  activateAddonSubscription,
  deactivateAddonSubscription,
  refreshCachedUserData,
  validatePin,
  loginPin,
  generatePin,
  deactivateSubscription,
  updateUserToken,
  updateUserCookie,
  checkEvoucherEligiblity,
  checkEvoucherValidation,
  checkEvoucherDeviceValidity,
  eVoucherProvision,
  getConcurrency,
  getPaymentHistory,
  getAllActiveSubscriptionsDeepQuery: toAllActiveSubscriptionsDeepQueryCache(
    fromAllActiveSubscriptionsDeepQueryCache(getAllActiveSubscriptionsDeepQuery)
  ),
  getTVODAssetsStatus,
  updateTVODAssetsStatus,
  getTVODProductPrices,
  getUserDiscountEligibility,
  reactivateSubscription,
  reactivateAddon,
  getAvatars: toAvatarsCache(fromAvatarsCache(getAvatars)),
  addProfile,
  deleteProfile,
  getCurrentProfile,
  updateCurrentProfile,
  deleteCurrentProfile,
  updateSelectedProfileFromAllProfiles,
  getAllProfiles,
  updateAllProfiles,
  addUpdatePin,
  verifyPin,
  disablePin,
  updateIndexDBUser,
  getProfile,
  verifyPassword
};
